import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import './BannerDisplay.scss';
import { ConditionalBanner } from '../../types';
import { useStores } from '../../../../hooks/use-stores';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

const BannerDisplay: React.FC = () => {
  const { t } = useTranslation();
  const [showExpandedMessage, setShowExpandedMessage] = useState(false);
  const { bannerStore } = useStores();
  const containerRef = useRef<HTMLDivElement>(null);

  const lastHeightRef = useRef<number>(-1);
  const rafIdRef = useRef<number | null>(null);

  const updateContainerHeight = useCallback(() => {
    if (!containerRef.current) return;

    const height = containerRef.current.clientHeight;

    if (lastHeightRef.current === height) return;

    lastHeightRef.current = height;

    rafIdRef.current = requestAnimationFrame(() => {
      bannerStore.setContainerHeight(height);
      document.documentElement.style.setProperty(
        '--banner-container-height',
        `${height}px`,
      );
    });
  }, [bannerStore]);

  useEffect(() => {
    updateContainerHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateContainerHeight();
    });

    const currentRef = containerRef.current;

    if (currentRef) {
      resizeObserver.observe(currentRef);
    }

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (currentRef) {
        resizeObserver.unobserve(currentRef);
      }
      resizeObserver.disconnect();
    };
  }, [updateContainerHeight]);

  return (
    <div className="banners-container" ref={containerRef}>
      {bannerStore.getBanners().map((banner: ConditionalBanner) => (
        <div
          key={banner.id}
          className={`banner banner-${banner.variant || 'warning'}`}
        >
          <div className="banner-content">
            <span>
              {banner.message}
              {showExpandedMessage && banner.expandedMessage}
            </span>

            {banner.expandedMessage && !showExpandedMessage && (
              <span>
                <a
                  onClick={() =>
                    setShowExpandedMessage(!showExpandedMessage)
                  }
                >
                  <b>{t('See more...')}</b>
                </a>
              </span>
            )}

            {banner.action && (
              <button
                onClick={banner.action.onClick}
                className="banner-action"
              >
                {banner.action.label}
              </button>
            )}
          </div>

          {banner.dismissible && (
            <button
              onClick={() =>
                bannerStore.removeConditionalBanner(banner.id)
              }
              className="banner-dismiss"
            >
              ✕
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default observer(BannerDisplay);
